import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { CallServiceAbstract } from "../call.service";
import { SettingsService } from "../../settings.service";
import { AuthenticationService } from "../../auth/Authentication/authentication.service";
import { NotificationService } from "../../notification.service";
import { StateService } from "../../state/state.service";
import { take } from "rxjs/operators";

export interface DocumentTreeNode {
  document: any;
  children: DocumentTreeNode[];
}

@Injectable({
  providedIn: "root",
})
export class GeneralDocumentsDocuments extends CallServiceAbstract {
  constructor(
    http: HttpClient,
    settings: SettingsService,
    authentication: AuthenticationService,
    notify: NotificationService,
    state: StateService
  ) {
    super(
      http,
      settings,
      authentication,
      notify,
      state,
      "General_Documents_Documents",
      "Документи"
    );
  }

  public async getDocumentsTreeByParentRelation(document: any, entityName: string | null = null): Promise<DocumentTreeNode | null> {
    const currentDocumentId = this.resolveDocumentId(document);
    if (!currentDocumentId) {
      return null;
    }

    const parentId = this.extractEntityId(document && document.Parent);
    const masterId = this.extractEntityId(document && document.MasterDocument);

    let rootId = currentDocumentId;
    if (masterId && masterId !== parentId) {
      rootId = masterId;
    }

    // зареждаме root документа отново, за да гарантираме, че DocumentType.UserStatuses и UserState са разширени,
    // независимо от това какви полета е имал оригиналният обект, подаден отвън
    const rootDocument = await this.getDocumentById(rootId);

    if (!rootDocument) {
      return {
        document: {
          Id: rootId,
          "@odata.id": this.toDocumentOdataId(rootId),
          _statusChangeAllowed: false,
        },
        children: [],
      };
    }

    const visited = new Set<string>();
    return await this.buildTreeRecursive(rootDocument, visited, entityName);
  }

  private async buildTreeRecursive(document: any, visited: Set<string>, entityName: string | null = null): Promise<DocumentTreeNode> {
    const documentId = this.resolveDocumentId(document);
    if (!documentId) {
      return {
        document: document,
        children: [],
      };
    }

    if (visited.has(documentId)) {
      return {
        document: document,
        children: [],
      };
    }

    visited.add(documentId);

    // документ от друг entity тип спрямо подаденото (напр. MasterDocument сочи към друг тип документ) -
    // се визуализира в дървото, но без право за смяна на статус
    document._statusChangeAllowed = !entityName || document.EntityName === entityName;

    const children = await this.getChildrenByParentId(documentId, entityName);
    const childNodes: DocumentTreeNode[] = [];

    for (let i = 0; i < children.length; i++) {
      const childNode = await this.buildTreeRecursive(children[i], visited, entityName);
      childNodes.push(childNode);
    }

    return {
      document: document,
      children: childNodes,
    };
  }

  private async getChildrenByParentId(parentId: string, entityName: string | null = null): Promise<any[]> {
    const call = JSON.parse(JSON.stringify(this.stateTemplate));
    call.filter.filters.push({
      field: "Parent",
      operator: "eq",
      value: "'" + this.toDocumentOdataId(parentId) + "'",
    });

    if (entityName && entityName.length > 0) {
      call.filter.filters.push({
        field: "EntityName",
        operator: "eq",
        value: "'" + entityName + "'",
      });
    }

    call.expand = ['DocumentType($expand=UserStatuses)', 'UserStatus'];

    call.take = 1000;

    const res = <any>await this.query(call).pipe(take(1)).toPromise();
    if (!res || !Array.isArray(res.value)) {
      return [];
    }

    return res.value;
  }

  private async getDocumentById(documentId: string): Promise<any | null> {
    const call = JSON.parse(JSON.stringify(this.stateTemplate));
    call.filter.filters.push({
      field: "Id",
      operator: "eq",
      value: documentId,
    });

    call.expand = ['DocumentType($expand=UserStatuses)', 'UserStatus'];
    call.take = 1;

    const res = <any>await this.query(call).pipe(take(1)).toPromise();
    if (!res || !Array.isArray(res.value) || res.value.length === 0) {
      return null;
    }

    return res.value[0];
  }

  public resolveDocumentId(document: any): string | null {
    if (!document) {
      return null;
    }

    if (document.DocumentId) {
      return String(document.DocumentId);
    }

    if (document.Id) {
      return String(document.Id);
    }

    return this.extractEntityId(document["@odata.id"]);
  }

  private extractEntityId(entityRef: any): string | null {
    if (!entityRef) {
      return null;
    }

    if (typeof entityRef === "string") {
      const match = entityRef.match(/\(([^)]+)\)$/);
      if (match && match[1]) {
        return match[1];
      }

      return entityRef;
    }

    if (entityRef.Id) {
      return String(entityRef.Id);
    }

    if (entityRef.DocumentId) {
      return String(entityRef.DocumentId);
    }

    if (entityRef["@odata.id"]) {
      return this.extractEntityId(entityRef["@odata.id"]);
    }

    return null;
  }

  private toDocumentOdataId(documentId: string): string {
    return "General_Documents_Documents(" + documentId + ")";
  }
}
